import { Flex } from '@chakra-ui/react';
import { Modal, Button, useToast } from '@morressier/alembic';
import { useEditorialUploadCopyrightForm } from 'src/api/commands';
import { IDecisionModalPropsForSubmittingAuthor } from '../decisionModals/decisionModalHelper.js';
import { FormProvider, useForm } from 'react-hook-form';
import { useWorkflow } from 'src/hooks/useWorkflow';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { CustomFormFieldDefinition } from '@publishing-engine/domain/types/article';
import { CustomFormFieldComponent } from 'src/components/FormField';
import { GenericFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/generic-form-fields/generic-form-fields';
import { generateValidationSchema } from 'src/utils/validation';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { queryClient } from '../../../utils/queryClient.js';

export const EditorialUploadCopyrightFormModal = ({
  onClose,
  submissionId,
  submissionContextId,
  authorEmail
}: IDecisionModalPropsForSubmittingAuthor) => {
  const {
    forms: { editorialUploadCopyright },
    articleWorkflow
  } = useWorkflow();

  if (!editorialUploadCopyright) {
    return <></>;
  }

  const toast = useToast();

  const { refetchSubmission, organizationId } = useSubmissionData();
  const editorialUploadCopyrightMutation = useEditorialUploadCopyrightForm({
    submissionId,
    organizationId,
    submissionContextId,
    articleWorkflowId: articleWorkflow.id,
    invalidateSubmissionQueries: refetchSubmission
  });

  const uploadCopyrightFields = new GenericFormFields(editorialUploadCopyright);

  const config = {
    CopyrightFormFile: uploadCopyrightFields.getFieldConfig(
      'CopyrightFormFile'
    ) as CustomFormFieldDefinition
  };
  const schema = generateValidationSchema(config);

  const methods = useForm({
    defaultValues: {
      copyrightForm: {
        file: {}
      }
    },
    resolver: ajvResolver(schema)
  });
  const { handleSubmit, getValues } = methods;

  const submit = async () => {
    const formValues = getValues();
    const payload = {
      ...formValues,
      copyrightForm: {
        ...formValues.copyrightForm,
        email: authorEmail,
        isSubmittingAuthor: true
      }
    };

    const res = await editorialUploadCopyrightMutation.mutateAsync(payload);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: 'Copyright and Publication Agreement Uploaded'
      });
      queryClient.invalidateQueries([
        'submitting-author-only-copyright-form-editorial',
        { submissionId, roleType: 'editorial' }
      ]);
      onClose();
    } else {
      toast({
        variant: 'error',
        title: 'Error uploading Copyright And Publication Agreement'
      });
    }
  };

  return (
    <Modal isOpen onClose={onClose} width="720px">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submit)}>
          <Flex padding={8} gap={6} flexDirection="column" overflow="auto">
            {config.CopyrightFormFile && (
              <CustomFormFieldComponent config={config.CopyrightFormFile} />
            )}

            <Flex gap={2} justifyContent="flex-end">
              <Button onClick={onClose} variant="secondary">
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={editorialUploadCopyrightMutation.isLoading}
              >
                Upload
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
